// frontend/packages/business-console/src/components/content/BlogContextCard.jsx
//
// Lightweight structured preview of a blog post's frontmatter, rendered
// alongside the editor so authors can visually check what they've entered.
//
// This is intentionally NOT a live HTML preview — we don't render the
// markdown body, we don't apply the site's theme, and we don't attempt to
// match the live layout. It's a structured display of the key fields so
// the author can confirm the cover image, title, date, and description
// at a glance before publishing.

import React, { useMemo } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import { buildCloudinaryUrl } from "../../utils/mediaToken";
import { useMediaConfig } from "../../contexts/MediaConfigContext";

/**
 * Safely read a string-ish field from the frontmatter object.
 */
const readField = (obj, key) => {
  const v = obj?.[key];
  if (v == null) return "";
  return String(v);
};

/**
 * Format a date value for display. Accepts a Date, ISO string, or
 * YYYY-MM-DD string. Falls back to the raw string if it can't parse.
 */
const formatDate = (raw) => {
  if (!raw) return "";
  const s = String(raw);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  try {
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return s;
  }
};

export default function BlogContextCard({ frontmatter }) {
  const { cloudName } = useMediaConfig();

  const title = readField(frontmatter, "title");
  const description = readField(frontmatter, "description");
  const date = readField(frontmatter, "date");
  const category = readField(frontmatter, "category");
  const author = readField(frontmatter, "author");
  const coverImageRaw = readField(frontmatter, "coverImage");

  const coverUrl = useMemo(() => {
    if (!coverImageRaw.startsWith("cld:")) return "";
    return buildCloudinaryUrl(coverImageRaw, cloudName, {
      width: 600,
      height: 340,
      mediaType: "image",
    });
  }, [cloudName, coverImageRaw]);

  // Don't render the card at all if there's nothing meaningful to preview.
  const hasContent = Boolean(title || description || coverImageRaw || date);
  if (!hasContent) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography
        variant="caption"
        sx={{ opacity: 0.6, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}
      >
        Preview
      </Typography>

      <Box sx={{ mt: 1.5 }}>
        {/* Cover image (or placeholder) */}
        <Box
          sx={{
            aspectRatio: "16 / 9",
            width: "100%",
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "action.hover",
            display: "grid",
            placeItems: "center",
            mb: 1.5,
          }}
        >
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={readField(frontmatter, "coverImageAlt") || title || ""}
              loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Stack alignItems="center" spacing={0.5} sx={{ color: "action.disabled" }}>
              <ImageOutlinedIcon sx={{ fontSize: 40 }} />
              <Typography variant="caption">No cover image</Typography>
            </Stack>
          )}
        </Box>

        {/* Category chip + date row */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 0.5 }}>
          {category ? (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {category}
            </Typography>
          ) : null}
          {date ? (
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              {formatDate(date)}
            </Typography>
          ) : null}
        </Stack>

        {/* Title */}
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
          {title || <span style={{ opacity: 0.4 }}>Untitled post</span>}
        </Typography>

        {/* Description */}
        {description ? (
          <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.75 }}>
            {description}
          </Typography>
        ) : null}

        {/* Author */}
        {author ? (
          <Typography variant="caption" sx={{ opacity: 0.6, display: "block", mt: 1 }}>
            By {author}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}
